/**
 * One composable, four states, no afterthoughts.
 *
 * Every screen in this section reads from the BFF, and every one of them can be
 * loading, broken, empty or ready. Treating those as one enum — instead of three
 * booleans a page is free to forget — is what stops "silently blank" from being the
 * default failure mode. `WasloPageLayout` branches on exactly this enum.
 *
 * Aborts on unmount and on re-entry, so a slow request can never write into a page the
 * customer has already left.
 */

import { ref, computed, onMounted, onScopeDispose } from 'vue';

import { WASLO_ERROR_CODES } from '../api/client.js';
import { useWasloI18n } from '../i18n.js';

export const WASLO_STATE = {
  IDLE: 'idle',
  LOADING: 'loading',
  ERROR: 'error',
  /**
   * The account has no assistant. Its own state, not a flavour of ERROR, because it is
   * not a failure: nothing is broken, nothing was lost, and retrying cannot help.
   *
   * Found in production. Every page derived its message as
   * `error ? t('WASLO.COMMON.ERROR_BODY') : ''`, throwing the error's `code` away — so an
   * account with no tenant (the BFF answers 404 `not_provisioned`) was told "Check your
   * connection and try again", forever, about a connection that was fine. The taxonomy
   * carried `not_provisioned` the whole time; fifteen call sites discarded it.
   */
  NOT_PROVISIONED: 'not_provisioned',
  EMPTY: 'empty',
  READY: 'ready',
};

const isBlank = value => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

/**
 * @param {(signal: AbortSignal) => Promise<unknown>} loader
 * @param {{ immediate?: boolean, isEmpty?: (data: unknown) => boolean }} [options]
 */
export const useWasloApi = (loader, options = {}) => {
  const { immediate = true, isEmpty = isBlank } = options;
  const { t } = useWasloI18n();

  const data = ref(null);
  const error = ref(null);
  const isLoading = ref(false);
  const hasLoaded = ref(false);

  let controller = null;

  const abortInFlight = () => {
    if (controller) {
      controller.abort();
      controller = null;
    }
  };

  const load = async () => {
    abortInFlight();
    const current = new AbortController();
    controller = current;

    isLoading.value = true;
    error.value = null;

    try {
      const result = await loader(current.signal);
      if (current.signal.aborted) return;
      data.value = result;
      hasLoaded.value = true;
    } catch (caught) {
      if (current.signal.aborted || caught?.name === 'AbortError') return;
      error.value = caught;
    } finally {
      if (controller === current) {
        controller = null;
        isLoading.value = false;
      }
    }
  };

  const state = computed(() => {
    // `error.value?.code &&` is load-bearing, not defensive noise: without it, a page with
    // NO error compares `undefined === undefined` and every screen in the section renders
    // "no assistant on this workspace". Caught by the suite the moment it was written.
    if (
      error.value?.code &&
      error.value.code === WASLO_ERROR_CODES.NOT_PROVISIONED
    ) {
      return WASLO_STATE.NOT_PROVISIONED;
    }
    if (error.value) return WASLO_STATE.ERROR;
    // A refresh over data we already have is not a loading screen — it would flash the
    // spinner over a page that is perfectly readable.
    if (isLoading.value && !hasLoaded.value) return WASLO_STATE.LOADING;
    if (!hasLoaded.value) return WASLO_STATE.IDLE;
    return isEmpty(data.value) ? WASLO_STATE.EMPTY : WASLO_STATE.READY;
  });

  if (immediate) {
    onMounted(load);
  }

  onScopeDispose(abortInFlight);

  return {
    data,
    error,
    isLoading,
    hasLoaded,
    state,
    // IDLE COUNTS AS FETCHING, AND THIS IS THE WHOLE POINT OF THE FLAG.
    //
    // `onMounted(load)` runs AFTER the first render, so every page that reads from the
    // BFF is IDLE — not LOADING — at first paint. A `state === LOADING` test therefore
    // returns false on the one frame where there is provably no data, and the page falls
    // straight through to its body slot: Overview rendered "0 credits / Off", Personality
    // rendered five empty boxes and a Save button, both before a single request had been
    // made. Each page fixed it locally with its own computed, and the next page inherited
    // the trap again — so it is fixed HERE, once, and the pages read this flag.
    //
    // A refresh over data we already have is deliberately NOT fetching (see `state`): the
    // spinner belongs on the frame that has nothing to show, never over a readable page.
    isFetching: computed(
      () =>
        state.value === WASLO_STATE.IDLE || state.value === WASLO_STATE.LOADING
    ),
    isEmptyState: computed(() => state.value === WASLO_STATE.EMPTY),
    hasError: computed(() => state.value === WASLO_STATE.ERROR),
    isNotProvisioned: computed(
      () => state.value === WASLO_STATE.NOT_PROVISIONED
    ),
    // The message a page shows, derived HERE from the error's code rather than at each
    // call site. A page that renders this cannot tell the customer the wrong thing about
    // why the screen is blank, and a new code only has to be handled once.
    errorMessage: computed(() => {
      if (state.value !== WASLO_STATE.ERROR) return '';
      return error.value?.code === WASLO_ERROR_CODES.UNAUTHORIZED
        ? t('WASLO.COMMON.ERROR_SESSION')
        : t('WASLO.COMMON.ERROR_BODY');
    }),
    reload: load,
  };
};
